<template>
    <main class="flex justify-center bg-gray-50">
        <HeaderComponent />
        <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">
            <!-- Notifications -->
            <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
                <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                    {{ errorMessage }}
                </div>
                <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                    {{ successMessage }}
                </div>
            </div>

            <!-- Dashboard Header -->
            <div class="mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                <div>
                    <h1 class="text-2xl font-bold text-gray-800">Tableau de bord Client</h1>
                    <p class="text-sm text-gray-500">Statistiques et analyse des ventes</p>
                </div>
                <button v-if="user?.isSuper" @click="openBroadcastModal" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-md self-start">
                    <i class="fas fa-envelope mr-2"></i> Envoyer un email
                </button>
            </div>

            <!-- Onglets -->
            <div class="inline-flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 mb-6">
                <button @click="activeTab = 'achats'" :class="activeTab === 'achats' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-semibold transition-colors">Achats</button>
                <button @click="switchToComptes" :class="activeTab === 'comptes' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-semibold transition-colors">
                    Comptes créés <span v-if="comptesTotal" class="ml-1 text-xs">({{ comptesTotal }})</span>
                </button>
            </div>

            <template v-if="activeTab === 'achats'">
            <!-- Stats Cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <!-- Total Clients Card -->
                <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-gray-500">Nouveaux clients</p>
                            <p class="text-3xl font-bold text-gray-800 mt-1">{{ totalOrders }}</p>
                        </div>
                        <div class="p-3 rounded-full bg-blue-100 text-blue-600">
                            <i class="fas fa-users text-xl"></i>
                        </div>
                    </div>
                </div>
            
                <!-- Boutique Sales Card -->
                <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-gray-500">Achats en boutique</p>
                            <p class="text-3xl font-bold text-gray-800 mt-1">{{ boutiqueOrders }}</p>
                        </div>
                        <div class="p-3 rounded-full bg-green-100 text-green-600">
                            <i class="fas fa-store text-xl"></i>
                        </div>
                    </div>
                </div>
            
                <!-- Online Sales Card -->
                <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-gray-500">Achats en ligne</p>
                            <p class="text-3xl font-bold text-orange-600 mt-1">{{ onlineOrders }}</p>
                        </div>
                        <div class="p-3 rounded-full bg-orange-100 text-orange-600">
                            <i class="fas fa-globe text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Clients Table -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 h-auto">
                <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 class="text-lg font-semibold text-gray-800">Liste des clients</h2>
                    <div class="relative">
                        <input 
                            v-model="searchQuery" 
                            @input="filterClients" 
                            type="text" 
                            placeholder="Rechercher un client..." 
                            class="pl-8 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                    </div>
                </div>
                
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type d'achat</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr 
                                v-for="(client) in clients" 
                                :key="client._id" 
                                class="hover:bg-gray-50"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                                            <i class="fas fa-user"></i>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-gray-900">
                                                {{ client.client?.prenoms }} {{ client.client?.nom }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">{{ client.client?.email || '-' }}</div>
                                    <div class="text-sm text-gray-500">{{ client.client?.numero || '-' }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span 
                                        class="px-2 py-1 text-xs rounded-full"
                                        :class="{
                                            'bg-orange-100 text-orange-800': client.vendeur?.boutique === 'En ligne',
                                            'bg-green-100 text-green-800': client.vendeur?.boutique !== 'En ligne'
                                        }"
                                    >
                                        {{ client.vendeur?.boutique === 'En ligne' ? 'En ligne' : 'En boutique' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ formatDate(client.createdAt) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <!-- Empty state -->
                    <div v-if="clients.length === 0 && !isLoading" class="p-8 text-center">
                        <i class="fas fa-user-slash text-4xl text-gray-300 mb-3"></i>
                        <p class="text-gray-500">Aucun client trouvé</p>
                    </div>
                </div>
                
                <!-- Pagination -->
                <div v-if="totalItems > 0" class="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <!-- Mobile version -->
                    <div class="flex-1 flex justify-between sm:hidden">
                        <button 
                            @click="goToPage(currentPage - 1)" 
                            :disabled="!hasPreviousPage || isLoading"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            :class="{'opacity-50 cursor-not-allowed': !hasPreviousPage || isLoading}"
                        >
                            Précédent
                        </button>
                        <span class="text-sm text-gray-700 mx-4 my-2">
                            Page {{ currentPage }} sur {{ totalPages }}
                        </span>
                        <button 
                            @click="goToPage(currentPage + 1)" 
                            :disabled="!hasNextPage || isLoading"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            :class="{'opacity-50 cursor-not-allowed': !hasNextPage || isLoading}"
                        >
                            Suivant
                        </button>
                    </div>
                    
                    <!-- Desktop version -->
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700">
                                Affichage de <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span> 
                                à <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, totalItems) }}</span> 
                                sur <span class="font-medium">{{ totalItems }}</span> clients
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button 
                                    @click="goToPage(currentPage - 1)" 
                                    :disabled="!hasPreviousPage || isLoading"
                                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                    :class="{'opacity-50 cursor-not-allowed': !hasPreviousPage || isLoading}"
                                >
                                    <span class="sr-only">Précédent</span>
                                    <i class="fas fa-chevron-left"></i>
                                </button>
                                
                                <!-- Pages -->
                                <template v-for="page in visiblePages" :key="page">
                                    <button 
                                        @click="goToPage(page)"
                                        :class="{
                                            'bg-blue-50 border-blue-500 text-blue-600 z-10': currentPage === page,
                                            'bg-white border-gray-300 text-gray-500 hover:bg-gray-50': currentPage !== page
                                        }"
                                        class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                                        :disabled="isLoading"
                                    >
                                        {{ page }}
                                    </button>
                                </template>
                                
                                <button 
                                    @click="goToPage(currentPage + 1)" 
                                    :disabled="!hasNextPage || isLoading"
                                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                    :class="{'opacity-50 cursor-not-allowed': !hasNextPage || isLoading}"
                                >
                                    <span class="sr-only">Suivant</span>
                                    <i class="fas fa-chevron-right"></i>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>

                <!-- Loading indicator -->
                <div v-if="isLoading" class="text-center py-4">
                    <i class="fas fa-spinner fa-spin text-blue-500"></i>
                    <span class="ml-2 text-gray-600">Chargement...</span>
                </div>
            </div>
            </template>

            <!-- Onglet Comptes créés (comptes réellement inscrits sur la plateforme, whatsapp + mot de passe) -->
            <template v-else>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 h-auto">
                <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 class="text-lg font-semibold text-gray-800">Comptes clients créés</h2>
                    <div class="relative">
                        <input
                            v-model="comptesSearch"
                            @input="debouncedFetchComptes"
                            type="text"
                            placeholder="Rechercher (nom, whatsapp, email)..."
                            class="pl-8 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                    </div>
                </div>

                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Créé le</th>
                                <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="c in comptes" :key="c._id" @click="openClientDetail(c)" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ c.nom }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{{ c.whatsapp }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ c.email || '-' }}</td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="c.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'" class="px-2 py-1 text-xs rounded-full font-medium">{{ c.isActive ? 'Actif' : 'Bloqué' }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(c.createdAt) }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-center" @click.stop>
                                    <div class="flex justify-center gap-2">
                                        <button
                                            @click="toggleClientActive(c)"
                                            :class="c.isActive ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-green-100 text-green-700 hover:bg-green-200'"
                                            class="text-xs px-3 py-1.5 rounded-full font-semibold transition-colors whitespace-nowrap"
                                        >
                                            <i :class="c.isActive ? 'fa-lock' : 'fa-lock-open'" class="fas mr-1"></i>
                                            {{ c.isActive ? 'Bloquer' : 'Débloquer' }}
                                        </button>
                                        <button @click="deleteClientAccount(c)" class="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center" title="Supprimer le compte">
                                            <i class="fas fa-trash text-xs text-red-500"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="comptes.length === 0 && !isLoadingComptes" class="p-8 text-center">
                        <i class="fas fa-user-slash text-4xl text-gray-300 mb-3"></i>
                        <p class="text-gray-500">Aucun compte trouvé</p>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="comptesTotalPages > 1" class="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <p class="text-sm text-gray-700">Page {{ comptesPage }} sur {{ comptesTotalPages }} • {{ comptesTotal }} compte(s)</p>
                    <div class="flex gap-2">
                        <button @click="goToComptesPage(comptesPage - 1)" :disabled="comptesPage <= 1 || isLoadingComptes" class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Précédent</button>
                        <button @click="goToComptesPage(comptesPage + 1)" :disabled="comptesPage >= comptesTotalPages || isLoadingComptes" class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Suivant</button>
                    </div>
                </div>

                <div v-if="isLoadingComptes" class="text-center py-4">
                    <i class="fas fa-spinner fa-spin text-blue-500"></i>
                    <span class="ml-2 text-gray-600">Chargement...</span>
                </div>
            </div>
            </template>

            <!-- Modale : envoi d'un email groupé -->
            <div v-if="showBroadcastModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
                <div class="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="text-xl font-bold text-gray-800">Envoyer un email à tous les clients</h3>
                            <button @click="showBroadcastModal = false" class="text-gray-400 hover:text-gray-600"><i class="fas fa-times"></i></button>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Formulaire -->
                            <div class="space-y-3">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Objet</label>
                                    <input v-model="broadcastSubject" type="text" placeholder="Ex: Nouvelle collection disponible !" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm">
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
                                    <textarea v-model="broadcastContent" rows="10" placeholder="Écrivez votre message ici..." class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm resize-none"></textarea>
                                    <p class="text-xs text-gray-400 mt-1">Le logo et l'habillage Pomme City sont ajoutés automatiquement.</p>
                                </div>
                                <button @click="previewBroadcast" :disabled="isPreviewing || !broadcastSubject || !broadcastContent" class="w-full bg-gray-800 text-white py-2 rounded-lg font-medium hover:bg-gray-900 disabled:opacity-50 flex justify-center items-center gap-2 text-sm">
                                    <i v-if="!isPreviewing" class="fas fa-eye"></i>
                                    <span v-if="isPreviewing" class="loader-small"></span>
                                    {{ isPreviewing ? 'Génération...' : 'Aperçu' }}
                                </button>
                            </div>

                            <!-- Aperçu -->
                            <div class="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                                <div class="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-500">
                                    Aperçu
                                    <span v-if="broadcastPreview" class="ml-1 text-orange-600">— {{ broadcastPreview.count }} destinataire(s)</span>
                                </div>
                                <div v-if="!broadcastPreview" class="flex-1 flex items-center justify-center text-center text-xs text-gray-400 p-6">
                                    Renseignez l'objet et le contenu, puis cliquez sur "Aperçu" pour voir le rendu exact avant l'envoi.
                                </div>
                                <iframe v-else :srcdoc="broadcastPreview.html" class="w-full flex-1 min-h-[300px] bg-white"></iframe>
                            </div>
                        </div>

                        <!-- Choix de la tranche (limite Brevo : 300 mails/jour) -->
                        <div v-if="broadcastPreview && broadcastPreview.totalTranches > 0" class="mt-5 pt-4 border-t border-gray-100">
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                Tranche à envoyer
                                <span class="text-xs text-gray-400 font-normal">(limite Brevo : 300 mails/jour — envoyez une tranche de {{ broadcastPreview.trancheSize }} par jour)</span>
                            </label>
                            <select v-model.number="selectedTranche" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm">
                                <option v-for="t in broadcastPreview.totalTranches" :key="t" :value="t">
                                    Tranche {{ t }} ({{ (t - 1) * broadcastPreview.trancheSize + 1 }}–{{ Math.min(t * broadcastPreview.trancheSize, broadcastPreview.count) }} sur {{ broadcastPreview.count }})
                                </option>
                            </select>
                        </div>

                        <div class="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                            <button @click="showBroadcastModal = false" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">Annuler</button>
                            <button @click="sendBroadcast" :disabled="!broadcastPreview || isSending" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 flex justify-center items-center gap-2">
                                <span v-if="isSending" class="loader-small"></span>
                                <i v-else class="fas fa-paper-plane"></i>
                                {{ broadcastPreview ? `Envoyer la tranche ${selectedTranche} (${trancheCount} destinataire(s))` : 'Générez un aperçu avant d\'envoyer' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modale : détail d'un compte client (achats + plans) -->
            <div v-if="showClientDetailModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4" @click.self="showClientDetailModal = false">
                <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="text-xl font-bold text-gray-800">Détail du compte</h3>
                            <button @click="showClientDetailModal = false" class="text-gray-400 hover:text-gray-600"><i class="fas fa-times"></i></button>
                        </div>

                        <div v-if="isLoadingClientDetail" class="text-center py-16">
                            <i class="fas fa-spinner fa-spin text-blue-500 text-2xl"></i>
                        </div>

                        <template v-else-if="clientDetail">
                            <div class="bg-gray-50 rounded-lg p-4 mb-5">
                                <p class="font-bold text-gray-800">{{ clientDetail.client.nom }}</p>
                                <p class="text-sm text-gray-500">{{ clientDetail.client.whatsapp }}<span v-if="clientDetail.client.email"> • {{ clientDetail.client.email }}</span></p>
                                <p class="text-xs text-gray-400 mt-1">Compte créé le {{ formatFullDate(clientDetail.client.createdAt) }}</p>
                            </div>

                            <h4 class="font-semibold text-gray-800 mb-2">Achats effectués ({{ clientDetail.achats.length }})</h4>
                            <div v-if="clientDetail.achats.length === 0" class="text-sm text-gray-400 mb-5">Aucun achat trouvé.</div>
                            <div v-else class="space-y-2 mb-5">
                                <div v-for="a in clientDetail.achats" :key="a._id" class="flex justify-between items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg text-sm">
                                    <div class="min-w-0">
                                        <p class="font-medium text-gray-800 truncate">{{ a.produits.map(p => p.lotName).join(', ') }}</p>
                                        <p class="text-xs text-gray-400">{{ a.vendeur?.boutique || 'En ligne' }} • {{ formatFullDate(a.createdAt) }}</p>
                                    </div>
                                    <span class="font-bold text-emerald-600 whitespace-nowrap">{{ formatMoney(a.totalApresReduction) }}</span>
                                </div>
                            </div>

                            <h4 class="font-semibold text-gray-800 mb-2">Plans échelonnés ({{ clientDetail.plans.length }})</h4>
                            <div v-if="clientDetail.plans.length === 0" class="text-sm text-gray-400">Aucun plan.</div>
                            <div v-else class="space-y-2">
                                <div v-for="p in clientDetail.plans" :key="p._id" class="flex justify-between items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg text-sm">
                                    <div class="min-w-0">
                                        <p class="font-medium text-gray-800 truncate">{{ p.produit?.nom || p.nomDemande }}</p>
                                        <p class="text-xs text-gray-400">{{ formatFullDate(p.createdAt) }}</p>
                                    </div>
                                    <div class="text-right whitespace-nowrap">
                                        <span :class="planStatutClass(p.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ planStatutLabel(p.statut) }}</span>
                                        <p class="text-xs text-gray-500 mt-1">{{ formatMoney(p.montantPaye) }} / {{ formatMoney(p.prixTotal) }}</p>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup>
import HeaderComponent from "../components/HeaderComponent.vue";
import { ref, onMounted, computed, watch } from 'vue';

import { Chart, registerables } from 'chart.js';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { debounce } from 'lodash';
import { useAuthStore } from '@/store/auth';
import { useRouter } from 'vue-router';

Chart.register(...registerables);
const router = useRouter();
const authStore = useAuthStore();
const user = computed(() => authStore.user);

// Onglets
const activeTab = ref('achats');

// Data
const errorMessage = ref('');
const successMessage = ref('');
const totalOrders = ref(0);
const boutiqueOrders = ref(0);
const onlineOrders = ref(0);
const clients = ref([]);
const searchQuery = ref('');
const salesChart = ref(null);
const timeRange = ref('month');
const chartInstance = ref(null);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 20;
const totalItems = ref(0);
const totalPages = ref(1);
const isLoading = ref(false);

// Comptes créés sur la plateforme (distincts des "clients" dérivés des ventes)
const comptes = ref([]);
const comptesSearch = ref('');
const comptesPage = ref(1);
const comptesTotal = ref(0);
const comptesTotalPages = ref(1);
const isLoadingComptes = ref(false);
const comptesLoaded = ref(false);

// Détail d'un compte (achats + plans)
const showClientDetailModal = ref(false);
const clientDetail = ref(null);
const isLoadingClientDetail = ref(false);

// Mail groupé
const showBroadcastModal = ref(false);
const broadcastSubject = ref('');
const broadcastContent = ref('');
const broadcastPreview = ref(null);
const selectedTranche = ref(1);
const isPreviewing = ref(false);
const isSending = ref(false);
const trancheCount = computed(() => {
    if (!broadcastPreview.value) return 0;
    const { trancheSize, count } = broadcastPreview.value;
    const start = (selectedTranche.value - 1) * trancheSize;
    return Math.max(Math.min(trancheSize, count - start), 0);
});

// Computed
const hasPreviousPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(() => currentPage.value < totalPages.value);
const visiblePages = computed(() => {
    const range = 2;
    const start = Math.max(1, currentPage.value - range);
    const end = Math.min(totalPages.value, currentPage.value + range);
    
    const pages = [];
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
    
    return pages;
});

// Methods
function formatDate(date) {
    return format(new Date(date), 'dd/MM/yyyy', { locale: fr });
}

function formatFullDate(date) {
    return format(new Date(date), 'PPpp', { locale: fr });
}

async function fetchComptes() {
    isLoadingComptes.value = true;
    try {
        const { data } = await axios.get('/api/admin/clients', {
            params: { page: comptesPage.value, limit: 20, search: comptesSearch.value }
        });
        comptes.value = data.data || [];
        comptesTotal.value = data.total || 0;
        comptesTotalPages.value = data.totalPages || 1;
        comptesLoaded.value = true;
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des comptes clients';
        hideErrorMessageAfterDelay();
    } finally {
        isLoadingComptes.value = false;
    }
}
const debouncedFetchComptes = debounce(() => { comptesPage.value = 1; fetchComptes(); }, 300);
function goToComptesPage(page) {
    if (page >= 1 && page <= comptesTotalPages.value) {
        comptesPage.value = page;
        fetchComptes();
    }
}
function switchToComptes() {
    activeTab.value = 'comptes';
    if (!comptesLoaded.value) fetchComptes();
}

const formatMoney = (n) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0))} F`;
function planStatutLabel(s) { return { demande: 'En recherche', a_payer: 'À payer', en_cours: 'En cours', termine: 'Payé', vendu: 'Vendu', annule: 'Annulé' }[s] || s; }
function planStatutClass(s) {
    if (s === 'termine' || s === 'vendu') return 'bg-green-100 text-green-700';
    if (s === 'demande') return 'bg-yellow-100 text-yellow-700';
    if (s === 'annule') return 'bg-red-100 text-red-700';
    return 'bg-orange-100 text-orange-700';
}

async function openClientDetail(c) {
    showClientDetailModal.value = true;
    isLoadingClientDetail.value = true;
    clientDetail.value = null;
    try {
        const { data } = await axios.get(`/api/admin/clients/${c._id}/detail`);
        clientDetail.value = data;
    } catch (error) {
        errorMessage.value = error.response?.data?.error || 'Erreur lors du chargement du détail du compte.';
        hideErrorMessageAfterDelay();
        showClientDetailModal.value = false;
    } finally {
        isLoadingClientDetail.value = false;
    }
}

async function toggleClientActive(c) {
    const action = c.isActive ? 'bloquer' : 'débloquer';
    if (!confirm(`Voulez-vous vraiment ${action} le compte de ${c.nom} (${c.whatsapp}) ?${c.isActive ? ' Il ne pourra plus se connecter.' : ''}`)) return;
    try {
        const { data } = await axios.put(`/api/admin/clients/${c._id}/toggle-active`);
        c.isActive = data.isActive;
        successMessage.value = data.message;
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = error.response?.data?.error || 'Erreur lors du blocage/déblocage du compte.';
        hideErrorMessageAfterDelay();
    }
}

async function deleteClientAccount(c) {
    if (!confirm(`Supprimer définitivement le compte de ${c.nom} (${c.whatsapp}) ? Cette action est irréversible. Ses ventes et plans passés resteront visibles en historique, mais il ne pourra plus se connecter.`)) return;
    try {
        await axios.delete(`/api/admin/clients/${c._id}`);
        successMessage.value = 'Compte supprimé.';
        hideSuccessMessageAfterDelay();
        fetchComptes();
    } catch (error) {
        errorMessage.value = error.response?.data?.error || 'Erreur lors de la suppression du compte.';
        hideErrorMessageAfterDelay();
    }
}

async function fetchSalesData() {
    try {
        isLoading.value = true;
        const { data } = await axios.get('/api/sales/unique-client', {
            params: {
                page: currentPage.value,
                limit: itemsPerPage,
                search: searchQuery.value
            }
        });
        
        clients.value = data.data;
        totalItems.value = data.stats.total;
        totalPages.value = Math.ceil(data.stats.total / itemsPerPage);
        
        // Mise à jour des statistiques
        totalOrders.value = data.stats.total;
        boutiqueOrders.value = data.stats.boutique;
        onlineOrders.value = data.stats.online;
        
    } catch (error) {
        errorMessage.value = 'Erreur lors de la récupération des données clients';
        console.error(error);
        hideErrorMessageAfterDelay();
    } finally {
        isLoading.value = false;
    }
}


function updateStatistics(data) {
    totalOrders.value = data.length;
    boutiqueOrders.value = data.filter(sale => sale.vendeur?.boutique !== 'En ligne').length;
    onlineOrders.value = data.filter(sale => sale.vendeur?.boutique === 'En ligne').length;
}

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchSalesData();
    }
}

function filterClients() {
    currentPage.value = 1;
    fetchSalesData();
}

function setTimeRange(range) {
    timeRange.value = range;
    updateChart();
}

function updateChart() {
    if (chartInstance.value) {
        chartInstance.value.data = getChartData();
        chartInstance.value.update();
    }
}

function getChartData() {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const currentYear = new Date().getFullYear();
    
    const onlineData = Array(12).fill(0);
    const boutiqueData = Array(12).fill(0);
    
    clients.value.forEach(sale => {
        const date = new Date(sale.createdAt);
        const month = date.getMonth();
        
        if (timeRange.value === 'year' || date.getFullYear() === currentYear) {
            if (sale.vendeur?.boutique === 'En ligne') {
                onlineData[month]++;
            } else {
                boutiqueData[month]++;
            }
        }
    });
    
    return {
        labels: months,
        datasets: [
            {
                label: 'En ligne',
                data: onlineData,
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.3,
                fill: true
            },
            {
                label: 'En boutique',
                data: boutiqueData,
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                tension: 0.3,
                fill: true
            }
        ]
    };
}

function openBroadcastModal() {
    broadcastSubject.value = '';
    broadcastContent.value = '';
    broadcastPreview.value = null;
    selectedTranche.value = 1;
    showBroadcastModal.value = true;
}

// Un changement du sujet/contenu après un aperçu invalide celui-ci : on force un nouvel
// aperçu avant de pouvoir envoyer, pour être sûr que ce qui est confirmé = ce qui part.
watch([broadcastSubject, broadcastContent], () => { broadcastPreview.value = null; });

async function previewBroadcast() {
    isPreviewing.value = true;
    try {
        const { data } = await axios.post('/api/clients/broadcast-email/preview', {
            subject: broadcastSubject.value,
            content: broadcastContent.value
        });
        broadcastPreview.value = data;
        selectedTranche.value = 1;
    } catch (error) {
        errorMessage.value = error.response?.data?.error || 'Erreur lors de la génération de l\'aperçu.';
        hideErrorMessageAfterDelay();
    } finally {
        isPreviewing.value = false;
    }
}

async function sendBroadcast() {
    if (!broadcastPreview.value) return;
    if (!confirm(`Envoyer cet email à la tranche ${selectedTranche.value} (${trancheCount.value} destinataire(s)) ? Cette action est irréversible.`)) return;

    isSending.value = true;
    try {
        const { data } = await axios.post('/api/clients/broadcast-email', {
            subject: broadcastSubject.value,
            tranche: selectedTranche.value,
            content: broadcastContent.value
        });
        showBroadcastModal.value = false;
        successMessage.value = data.message;
        hideSuccessMessageAfterDelay();
    } catch (error) {
        errorMessage.value = error.response?.data?.error || 'Erreur lors de l\'envoi.';
        hideErrorMessageAfterDelay();
    } finally {
        isSending.value = false;
    }
}

function hideSuccessMessageAfterDelay() {
    setTimeout(() => {
        successMessage.value = ''; 
    }, 5000);
}

function hideErrorMessageAfterDelay() {
    setTimeout(() => {
        errorMessage.value = ''; 
    }, 10000);
}

// Lifecycle hooks
onMounted(async () => {
    await fetchSalesData();
    if (!user.value.isSuper) {
        router.push("/dashboard");
    }
});

// Watch for window resize to update chart
window.addEventListener('resize', () => {
    if (chartInstance.value) {
        chartInstance.value.resize();
    }
});
</script>

<style scoped>
.chart-container {
    position: relative;
    width: 100%;
    min-height: 300px;
}

.loader-small { border: 2px solid #f3f3f3; border-top: 2px solid #fff; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

@media (max-width: 640px) {
    .chart-container {
        height: 250px;
    }
}
</style>